// src/components/AppHeader.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { User as SupabaseUser } from "@supabase/supabase-js";

import logo from "@/assets/logo-circle.png";
import { supabase } from "../supabaseClient";
import { auth } from "@/lib/auth";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useTranslation } from "react-i18next";

export function AppHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authUser = auth.getCurrentUser();
  const [avatarInitial, setAvatarInitial] = useState<string>("?");

  // ⭐ 读取 sidebar 当前状态
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed"; // true = 只剩 icon 那种

  // 根据 user_profiles.full_name 优先，其次 email 的首字母
  useEffect(() => {
    const loadAvatar = async () => {
      const { data } = await supabase.auth.getUser();
      const sbUser: SupabaseUser | null = data.user ?? null;

      let initial = "";

      if (sbUser) {
        const { data: profile } = await supabase
          .from("user_profiles")
          .select("full_name")
          .eq("id", sbUser.id)
          .maybeSingle();

        const fullName = profile?.full_name?.trim();
        if (fullName) {
          initial = fullName[0]?.toUpperCase() ?? "";
        }
      }

      if (!initial) {
        const email = authUser?.email ?? sbUser?.email ?? "";
        if (email) {
          initial = email[0].toUpperCase();
        }
      }

      setAvatarInitial(initial || "?");
    };

    loadAvatar();
  }, [authUser]);

  // ⭐ 这里根据 sidebar 展开 / 折叠，给桌面端 header 不同的左 padding
  const desktopPadding = isCollapsed ? "md:pl-12" : "md:pl-64";

  return (
    <header
      className={
        `
        fixed inset-x-0 top-0 z-30
        h-14 h-14 
        border-b border-border
        bg-background/95 backdrop-blur
        supports-[backdrop-filter]:bg-background/60
      ` + " " + desktopPadding
      }
    >
      {/* 桌机版 Header：logo 在左，文字可见 */}
      <div className="hidden md:flex h-14 items-center px-4">
        {/* 左侧：Logo + 文案（位置会跟着 sidebar 一起移动） */}
        <div className="flex items-center gap-2 flex-1">
          <img src={logo} alt={t("app.name", { defaultValue: "Inner Gem" })} className="h-8 w-8" />
          <span className="font-semibold text-sm">{t("app.name", { defaultValue: "Inner Gem" })}</span>
        </div>

        {/* 右侧：用户头像按钮 */}
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold hover:brightness-110 transition"
        >
          {avatarInitial}
        </button>
      </div>

      {/* 手机版 Header：左边汉堡，Logo 居中 */}
      <div className="flex md:hidden h-14 items-center px-4">
        {/* 左侧：打开 sidebar 的汉堡按钮 */}
        <SidebarTrigger className="mr-2" />

        {/* 中间：Logo 置中 */}
        <div className="flex-1 flex justify-center">
          <img src={logo} alt={t("app.name", { defaultValue: "Inner Gem" })} className="h-8 w-8" />
        </div>

        {/* 右侧：用户头像按钮 */}
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold hover:brightness-110 transition"
        >
          {avatarInitial}
        </button>
      </div>
    </header>
  );
}
